import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ActionToastHost } from './ActionToastHost';
import { ActionToastKind } from './actionToastKinds';

export type ActiveActionToast = {
  id: string;
  kind: ActionToastKind;
  title: string;
  description?: string;
  timeout?: number;
};

export type NotifyOptions = {
  title: string;
  description?: string;
  timeout?: number;
};

type ActionToastContextValue = {
  notify: (kind: ActionToastKind, opts: NotifyOptions) => void;
};

const ActionToastContext = createContext<ActionToastContextValue | null>(null);

// Mounted once at the app root via createFrontendModule (see index.ts),
// the same AppRootWrapperBlueprint pattern packages/app/src/modules/
// sessionTimeout.tsx already uses for a different always-present overlay —
// this just wraps every page and renders the toast stack alongside it.
export const ActionToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ActiveActionToast[]>([]);
  const nextId = useRef(0);

  const notify = useCallback((kind: ActionToastKind, opts: NotifyOptions) => {
    const id = `action-toast-${++nextId.current}`;
    setToasts(prev => [...prev, { id, kind, ...opts }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // notify is already stable (useCallback with no deps) — memoizing the
  // wrapper object too means every consumer of useActionToast() only sees
  // a "new" value if notify itself ever actually changes, not on every
  // render of this provider (e.g. every time toasts changes).
  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <ActionToastContext.Provider value={value}>
      {children}
      <ActionToastHost toasts={toasts} onDismiss={dismiss} />
    </ActionToastContext.Provider>
  );
};

export const useActionToast = (): ActionToastContextValue => {
  const context = useContext(ActionToastContext);
  if (!context) {
    throw new Error('useActionToast must be used within ActionToastProvider');
  }
  return context;
};
